import { useCallback, useMemo, useRef, useEffect, useState } from "react";
import CodeMirror from "@uiw/react-codemirror";
import { sql, PostgreSQL } from "@codemirror/lang-sql";
import { linter, lintGutter } from "@codemirror/lint";
import { EditorView } from "@codemirror/view";

const DANGEROUS_KEYWORDS = ["DROP", "TRUNCATE", "ALTER", "DELETE", "INSERT", "UPDATE", "CREATE", "GRANT", "REVOKE"];
const REQUIRED_KEYWORD = "SELECT";

const UNBALANCED_MSG = "Unbalanced parentheses";
const EMPTY_MSG = "Query cannot be empty";
const MISSING_SELECT_MSG = "Query must contain a SELECT statement";
const UNCLOSED_STRING_MSG = "Unclosed string literal";
const TRAILING_COMMA_MSG = "Trailing comma before FROM/WHERE/GROUP/ORDER/HAVING/LIMIT";
const SEMICOLON_MSG = "Avoid trailing semicolons — queries are executed as-is";

function dangerousKeywordMsg(kw) {
  return `"${kw}" statements are not allowed in goal queries`;
}

function validateSql(doc) {
  const text = doc.toString();
  const diagnostics = [];

  if (!text.trim()) {
    diagnostics.push({ from: 0, to: 0, severity: "error", message: EMPTY_MSG });
    return diagnostics;
  }

  const upper = text.toUpperCase();

  if (!upper.includes(REQUIRED_KEYWORD)) {
    diagnostics.push({ from: 0, to: text.length, severity: "error", message: MISSING_SELECT_MSG });
  }

  for (const kw of DANGEROUS_KEYWORDS) {
    const regex = new RegExp(`\\b${kw}\\b`, "gi");
    let match;
    while ((match = regex.exec(text)) !== null) {
      diagnostics.push({
        from: match.index,
        to: match.index + kw.length,
        severity: "error",
        message: dangerousKeywordMsg(kw),
      });
    }
  }

  let depth = 0;
  for (let i = 0; i < text.length; i++) {
    if (text[i] === "(") depth++;
    if (text[i] === ")") depth--;
    if (depth < 0) {
      diagnostics.push({ from: i, to: i + 1, severity: "error", message: UNBALANCED_MSG });
      break;
    }
  }
  if (depth > 0) {
    const lastOpen = text.lastIndexOf("(");
    diagnostics.push({ from: lastOpen, to: lastOpen + 1, severity: "error", message: UNBALANCED_MSG });
  }

  let inSingle = false;
  let inDouble = false;
  let stringStart = -1;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (ch === "'" && !inDouble) {
      if (inSingle) { inSingle = false; } else { inSingle = true; stringStart = i; }
    } else if (ch === '"' && !inSingle) {
      if (inDouble) { inDouble = false; } else { inDouble = true; stringStart = i; }
    }
  }
  if (inSingle || inDouble) {
    diagnostics.push({ from: stringStart, to: text.length, severity: "error", message: UNCLOSED_STRING_MSG });
  }

  const trailingCommaPattern = /,\s*\b(FROM|WHERE|GROUP|ORDER|HAVING|LIMIT)\b/gi;
  let tcMatch;
  while ((tcMatch = trailingCommaPattern.exec(text)) !== null) {
    diagnostics.push({
      from: tcMatch.index,
      to: tcMatch.index + 1,
      severity: "warning",
      message: TRAILING_COMMA_MSG,
    });
  }

  const trimmed = text.trimEnd();
  if (trimmed.endsWith(";")) {
    diagnostics.push({
      from: text.lastIndexOf(";"),
      to: text.lastIndexOf(";") + 1,
      severity: "warning",
      message: SEMICOLON_MSG,
    });
  }

  return diagnostics;
}

const editorTheme = EditorView.theme({
  "&": {
    backgroundColor: "#ffffff",
    color: "#1e293b",
    fontSize: "14px",
    borderRadius: "12px",
  },
  ".cm-content": {
    caretColor: "#3b82f6",
    fontFamily: "ui-monospace, SFMono-Regular, 'SF Mono', Menlo, Consolas, monospace",
    padding: "12px 0",
  },
  ".cm-gutters": {
    backgroundColor: "#f8fafc",
    color: "#94a3b8",
    border: "none",
    borderRight: "1px solid #e2e8f0",
  },
  ".cm-activeLineGutter": { backgroundColor: "#f1f5f9" },
  ".cm-activeLine": { backgroundColor: "#f1f5f9" },
  ".cm-selectionBackground, ::selection": { backgroundColor: "#bfdbfe !important" },
  ".cm-cursor": { borderLeftColor: "#3b82f6" },
  "&.cm-focused .cm-selectionBackground": { backgroundColor: "#bfdbfe" },
  ".cm-line": { padding: "0 16px" },
  ".cm-tooltip": {
    backgroundColor: "#ffffff",
    color: "#334155",
    border: "1px solid #e2e8f0",
    borderRadius: "8px",
    boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
  },
  ".cm-tooltip-lint": { backgroundColor: "#ffffff" },
  ".cm-lintRange-error": { backgroundImage: "none", textDecoration: "wavy underline #dc2626" },
  ".cm-lintRange-warning": { backgroundImage: "none", textDecoration: "wavy underline #d97706" },
  ".cm-lint-marker-error": { content: "'●'", color: "#dc2626" },
  ".cm-lint-marker-warning": { content: "'●'", color: "#d97706" },
  ".cm-diagnostic-error": { borderLeft: "3px solid #dc2626", color: "#991b1b" },
  ".cm-diagnostic-warning": { borderLeft: "3px solid #d97706", color: "#92400e" },
  ".cm-placeholder": { color: "#94a3b8" },
});

export function SqlEditor({ value, onChange, disabled = false, placeholder = "Query will be generated automatically...", onValidationChange }) {
  const [hasErrors, setHasErrors] = useState(false);
  const prevHasErrors = useRef(false);

  const sqlLinter = useMemo(
    () =>
      linter(
        (view) => {
          const diagnostics = validateSql(view.state.doc);
          const errorsExist = diagnostics.some((d) => d.severity === "error");
          if (errorsExist !== prevHasErrors.current) {
            prevHasErrors.current = errorsExist;
            setHasErrors(errorsExist);
          }
          return diagnostics;
        },
        { delay: 300 },
      ),
    [],
  );

  useEffect(() => {
    if (onValidationChange) onValidationChange(hasErrors);
  }, [hasErrors, onValidationChange]);

  const extensions = useMemo(
    () => [
      sql({ dialect: PostgreSQL }),
      sqlLinter,
      lintGutter(),
      EditorView.lineWrapping,
      ...(disabled ? [EditorView.editable.of(false)] : []),
    ],
    [sqlLinter, disabled],
  );

  const handleChange = useCallback(
    (val) => {
      if (onChange) onChange(val);
    },
    [onChange],
  );

  return (
    <div className="rounded-xl overflow-hidden border border-gray-200 shadow-sm">
      <CodeMirror
        value={value}
        onChange={handleChange}
        extensions={extensions}
        theme={editorTheme}
        placeholder={placeholder}
        basicSetup={{
          lineNumbers: true,
          foldGutter: false,
          highlightActiveLine: true,
          bracketMatching: true,
          closeBrackets: true,
          autocompletion: true,
          indentOnInput: true,
        }}
        minHeight="120px"
      />
    </div>
  );
}
